import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MyVendorCardActionMenu from "./MyVendorCardActionMenu";
import MyVendorCardTagsField from "./MyVendorCardTagsField";
import MyVendorGroupedRequests from "./MyVendorGroupedRequests";
import { MyVendorRiskScoreBadgeWithDots } from "./MyVendorRiskScoreBadge";
import MyVendorRotatingLogo from "./MyVendorRotatingLogo";
import ServiceCategoryPill from "../ServiceCategoryPill";
import { vendorTypeColors } from "../../utils/constants";
import {
  MY_VENDOR_LIBRARY_STATUS_BUCKETS,
  MY_VENDOR_REQUEST_STATUS_BUCKETS,
  getVendorDisplayType,
  hasVendorRiskSummary,
  isManualOnlyVendor,
  LAST_QUALIFIED_DATE_COLUMN_ENABLED,
  RISK_SUMMARY_COLUMN_ENABLED,
} from "../../utils/myVendorsConstants";
import { isMyVendorProfileVisible } from "../../utils/gridOrgNameLinks";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function CardRequestTabButton({ label, count, active, disabled, onClick }) {
  const stateClass = disabled
    ? "cursor-not-allowed border-transparent text-gray-300"
    : active
      ? "border-indigo-500 text-indigo-600"
      : "border-transparent text-gray-500 hover:text-gray-700";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        if (!disabled) onClick();
      }}
      className={classNames(
        "-mb-px whitespace-nowrap border-b-2 px-0.5 py-2 text-xs font-medium transition-colors",
        stateClass
      )}
    >
      {label}
      <span className={classNames("ml-1.5", disabled ? "text-gray-300" : "text-gray-400")}>
        ({count})
      </span>
    </button>
  );
}

function VendorTypeRow({ type }) {
  const displayType = getVendorDisplayType(type);
  const dotClass = type && vendorTypeColors[type];

  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-sm text-gray-500">Type</span>
      <span className="flex items-center gap-2 pr-2 text-sm font-normal">
        {dotClass ? (
          <span className={classNames("inline-block h-2 w-2 rounded-full", dotClass)} />
        ) : null}
        {displayType}
      </span>
    </div>
  );
}

function GxpProfileRow({ applicableOnlyGxPs = [], demonstratedGxPs = [] }) {
  if (applicableOnlyGxPs.length === 0 && demonstratedGxPs.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 py-3">
      <span className="text-sm text-gray-500">GxP Profile</span>
      <div className="flex flex-wrap items-center gap-1">
        {applicableOnlyGxPs.map((gxp) => (
          <span
            key={`applicable-${gxp}`}
            className="inline-flex items-center rounded-md bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600"
          >
            {gxp}
          </span>
        ))}
        {applicableOnlyGxPs.length > 0 && demonstratedGxPs.length > 0 ? (
          <span className="mx-0.5 inline-block h-3 w-px bg-gray-300" aria-hidden="true" />
        ) : null}
        {demonstratedGxPs.map((gxp) => (
          <span
            key={`demonstrated-${gxp}`}
            className="inline-flex items-center rounded-md bg-slate-600 px-2 py-0.5 text-[10px] font-medium text-white"
          >
            {gxp}
          </span>
        ))}
      </div>
    </div>
  );
}

/** My Vendors vendor card (API-driven view model). */
export default function MyVendorCard({
  vendor,
  sponsorId = null,
  onVendorAction,
  onRequestAction,
  isSelected = false,
  onToggleSelection,
  isBookmarked = false,
  isBookmarkPending = false,
  isHidePending = false,
  isUnhidePending = false,
}) {
  const navigate = useNavigate();
  const [brokenLogo, setBrokenLogo] = useState(false);
  const [tabsExpanded, setTabsExpanded] = useState(false);

  useEffect(() => {
    setBrokenLogo(false);
  }, [vendor.logoUrl, vendor.id]);

  const isManualOnly = isManualOnlyVendor(vendor);
  const myRequests = vendor.myRequests || [];
  const libraryRequests = vendor.libraryRequests || [];

  const [activeRequestTab, setActiveRequestTab] = useState(() => {
    if (myRequests.length === 0 && libraryRequests.length > 0) return "library";
    return "requests";
  });

  const applicableOnlyGxPs = useMemo(() => {
    const applicable = vendor.applicableGxPs || [];
    const demonstrated = vendor.demonstratedGxPs || [];
    return applicable.filter((gxp) => !demonstrated.includes(gxp));
  }, [vendor.applicableGxPs, vendor.demonstratedGxPs]);

  const demonstratedGxPs = vendor.demonstratedGxPs || [];
  const serviceCapabilities = useMemo(() => {
    const caps = vendor.provider?.provider_capabilities;
    if (Array.isArray(caps) && caps.length > 0) return caps;
    return (vendor.serviceCategories || []).map((name) => ({ rfi_name: name }));
  }, [vendor.provider?.provider_capabilities, vendor.serviceCategories]);
  const showLogo = Boolean(vendor.logoUrl) && !brokenLogo;
  const hasRequestTabs = myRequests.length > 0 || libraryRequests.length > 0;

  // A vendor hidden in Discovery has no public profile to open.
  const canOpenProfile = isMyVendorProfileVisible(vendor);
  const handleProfileClick = () => navigate(`/provider/${vendor.id}/overview`);

  const handleRequestClick = (request) => {
    onRequestAction?.(request);
  };

  return (
    <div
      data-provider-card
      className="relative overflow-hidden rounded-lg border border-gray-200 bg-white pt-0 shadow-sm"
    >
      <div className="absolute right-6 top-4 z-[2]">
        <MyVendorCardActionMenu
          vendor={vendor}
          onAction={onVendorAction}
          isBookmarked={isBookmarked}
          isBookmarkPending={isBookmarkPending}
          isHidePending={isHidePending}
          isUnhidePending={isUnhidePending}
        />
      </div>

      <div className="relative flex items-center border-b border-gray-100 bg-gray-50 px-6 pb-5 pt-6 transition-colors duration-200 hover:bg-gray-100">
        <button
          type="button"
          onClick={onToggleSelection}
          className={classNames(
            "absolute left-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-200",
            isSelected
              ? "border-indigo-500 bg-indigo-100 shadow-md ring-2 ring-indigo-200"
              : "border-slate-300 bg-white hover:border-indigo-300 hover:bg-indigo-50"
          )}
          aria-label={isSelected ? "Deselect vendor" : "Select vendor"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            aria-hidden="true"
            className={classNames("h-5 w-5", isSelected ? "text-indigo-700" : "text-slate-400")}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
        </button>

        {isSelected ? (
          <div className="pointer-events-none absolute inset-0 bg-indigo-100 opacity-30" />
        ) : null}

        <button
          type="button"
          onClick={handleProfileClick}
          disabled={!canOpenProfile}
          className="flex flex-1 cursor-pointer justify-center disabled:cursor-default"
          aria-label={`View ${vendor.name} profile`}
        >
          <div className="flex h-20 w-48 items-center justify-center rounded-md">
            {showLogo ? (
              <MyVendorRotatingLogo
                vendorName={vendor.name}
                logoUrl={vendor.logoUrl}
                style={{ minHeight: "3.5rem", maxHeight: "4rem", maxWidth: "12rem" }}
                onExhausted={() => setBrokenLogo(true)}
              />
            ) : (
              <span className="flex h-16 w-48 items-center justify-center text-center text-sm font-normal text-gray-400 opacity-75 transition-colors duration-200 hover:text-gray-600">
                {vendor.name}
              </span>
            )}
          </div>
        </button>
      </div>

      <div className="divide-y divide-gray-100 px-6 py-2">
        {/* Prototype: row suppressed when zero risk contributors; hidden on manual-only. */}
        {RISK_SUMMARY_COLUMN_ENABLED && !isManualOnly && hasVendorRiskSummary(vendor) ? (
          <div className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Risk Summary</span>
              <div className="flex items-center pr-2">
                <MyVendorRiskScoreBadgeWithDots
                  score={vendor.riskSummary.score}
                  rating={vendor.riskSummary.rating}
                  size="sm"
                  showRatingText
                  showPercentage
                />
              </div>
            </div>
          </div>
        ) : null}

        <VendorTypeRow type={vendor.type} />

        {LAST_QUALIFIED_DATE_COLUMN_ENABLED && !isManualOnly && vendor.lastQualifiedDate ? (
          <div className="flex items-center justify-between py-3">
            <span className="text-sm text-gray-500">Last Qualified Date</span>
            <span className="pr-2 text-sm font-normal text-gray-900">{vendor.lastQualifiedDate}</span>
          </div>
        ) : null}

        {isManualOnly && vendor.primaryLocation ? (
          <div className="flex items-center justify-between py-3">
            <span className="text-sm text-gray-500">Primary Location</span>
            <span className="pr-2 text-sm font-normal text-gray-900">{vendor.primaryLocation}</span>
          </div>
        ) : null}

        {isManualOnly ? (
          <GxpProfileRow
            applicableOnlyGxPs={applicableOnlyGxPs}
            demonstratedGxPs={demonstratedGxPs}
          />
        ) : null}

        {isManualOnly && serviceCapabilities.length > 0 ? (
          <div className="flex flex-col gap-2 py-3">
            <span className="text-sm text-gray-500">Service Categories</span>
            <div className="flex flex-wrap gap-1">
              {serviceCapabilities.map((cap) => {
                const label = cap.rfi_name || cap.name;
                if (!label) return null;
                return (
                  <ServiceCategoryPill
                    key={cap.rfi_id ?? label}
                    name={label}
                    rfiId={cap.rfi_id}
                  />
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Custom Tags</span>
          </div>
          <MyVendorCardTagsField providerId={vendor.id} sponsorId={sponsorId} />
        </div>

        {!isManualOnly && hasRequestTabs ? (
          <div className="flex flex-col gap-2 py-3">
            <div className="flex items-center gap-4 border-b border-gray-100">
              <CardRequestTabButton
                label="My Requests"
                count={myRequests.length}
                active={activeRequestTab === "requests"}
                disabled={myRequests.length === 0}
                onClick={() => setActiveRequestTab("requests")}
              />
              <CardRequestTabButton
                label="Library Requests"
                count={libraryRequests.length}
                active={activeRequestTab === "library"}
                disabled={libraryRequests.length === 0}
                onClick={() => setActiveRequestTab("library")}
              />
            </div>

            {activeRequestTab === "requests" && myRequests.length > 0 ? (
              <MyVendorGroupedRequests
                items={myRequests}
                emptyLabel="No platform requests"
                expanded={tabsExpanded}
                onToggleExpanded={setTabsExpanded}
                statusBuckets={MY_VENDOR_REQUEST_STATUS_BUCKETS}
                onRequestClick={handleRequestClick}
              />
            ) : null}

            {activeRequestTab === "library" && libraryRequests.length > 0 ? (
              <MyVendorGroupedRequests
                items={libraryRequests}
                emptyLabel="No library requests"
                expanded={tabsExpanded}
                onToggleExpanded={setTabsExpanded}
                statusBuckets={MY_VENDOR_LIBRARY_STATUS_BUCKETS}
                inverted
                onRequestClick={handleRequestClick}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
